import { type FC, useState, useEffect, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { NftFeaturedCreatorTile } from './NftFeaturedCreatorTile/NftFeaturedCreatorTile.tsx';
import { Carousel } from 'dibs-carousel';
import { CarouselDots } from 'dibs-carousel/exports/CarouselDots.jsx';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { FormattedMessage } from 'dibs-react-intl';

import { type NftFeaturedCreators_viewer$key } from './__generated__/NftFeaturedCreators_viewer.graphql.ts';

import classNames from 'classnames';
import styles from './NftFeaturedCreators.scss';

const ITEMS_PER_PAGE = 3;

type Props = {
    viewer: NftFeaturedCreators_viewer$key | null | undefined;
};

export const NftFeaturedCreators: FC<Props> = ({ viewer: viewerRef }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const swiperInterval = useRef<NodeJS.Timeout | null>(null);
    const viewer = useFragment(
        graphql`
            fragment NftFeaturedCreators_viewer on Viewer {
                nftFeaturedCreatorsPage(rootId: $pageId) {
                    profiles {
                        ...NftFeaturedCreatorTile_nftProfile
                    }
                }
            }
        `,
        viewerRef
    );

    const profiles = viewer?.nftFeaturedCreatorsPage?.profiles?.map((profile, index) => {
        return <NftFeaturedCreatorTile nftProfile={profile || null} key={`profile-${index}`} />;
    });

    const totalItems = profiles?.length || 0;

    useEffect(() => {
        swiperInterval.current = setInterval(() => {
            if (currentIndex < totalItems) {
                setCurrentIndex(currentIndex + 1);
            } else {
                if (swiperInterval.current) {
                    clearInterval(swiperInterval.current);
                }
                setCurrentIndex(0);
            }
        }, 10000);
        return () => {
            if (swiperInterval.current) {
                clearInterval(swiperInterval.current);
            }
        };
    }, [currentIndex, setCurrentIndex, totalItems]);

    if (totalItems === 0) {
        return null;
    }

    return (
        <>
            <div className={styles.pageHeading}>
                <h2 className={styles.pageTitle}>
                    <FormattedMessage
                        id="nft.creators.featuredCreators.header"
                        defaultMessage="Featured Creators"
                    />
                </h2>
            </div>
            <div className={styles.carouselWrapper}>
                <div className={styles.hideOnMobile}>
                    <Carousel
                        totalItems={totalItems}
                        itemsPerPage={totalItems > ITEMS_PER_PAGE ? ITEMS_PER_PAGE : totalItems}
                        step={ITEMS_PER_PAGE}
                        onIndexChange={({ index }: { index: number }) => setCurrentIndex(index)}
                        renderItem={({ index }: { index: number }) => {
                            return profiles?.[index];
                        }}
                    />
                </div>
                <div
                    className={classNames(
                        {
                            [styles.fullWidth]: totalItems === 1,
                        },
                        styles.showOnMobile
                    )}
                >
                    <Swiper
                        itemsPerPage={1}
                        hasInfiniteLoop
                        onPageChange={(index: number) => setCurrentIndex(index)}
                        startingPage={currentIndex}
                        classNames={{ wrapper: styles.swiperCarousel, item: styles.listItem }}
                    >
                        {profiles}
                    </Swiper>
                    {totalItems > 1 && (
                        <CarouselDots
                            dotsPerPage={6}
                            index={currentIndex}
                            totalItems={totalItems}
                            isDark
                        />
                    )}
                </div>
            </div>
        </>
    );
};
